import { HotTable, HotColumn } from "@handsontable/react-wrapper";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import { formatISO } from "date-fns";
import { CellChange, ChangeSource } from "handsontable/common";
import {
    IntegrationTransactionUpdateBody,
    useIntegrationTransactionQuery,
    useIntegrationTransactionUpdateMutation,
} from "../../api/integrationTransaction.api";
import {
    FinancialDocument,
    ImporterErrors,
} from "../../common/types/financialDocument";
import { IntegrationAccount } from "../../common/types/integrationAccount";
import { useFinancialDocumentReviewMutation } from "../../api/financialDocument.api";
import { SpreadsheetDateEditor } from "../spreadsheet/SpreasheetDateEditor";
import styles from "./ImporterPage.module.scss";
import { isTransactionsProcessingFinished } from "./helpers";
import { ImporterBalanceSummary } from "./ImporterBalanceSummary";
import { ImporterContext } from "./importer.context";

interface Props {
    documentRowId: string;
    financialDocument: FinancialDocument;
    integrationAccount: IntegrationAccount;
}

export const ImporterDataPreview: React.FC<Props> = ({
    documentRowId,
    financialDocument,
    integrationAccount,
}) => {
    const { updateDocumentRow } = useContext(ImporterContext);
    const reviewed = useRef(false);

    const reviewMutation = useFinancialDocumentReviewMutation(
        financialDocument,
        (document) => {
            updateDocumentRow(documentRowId, {
                financialDocument: document,
            });
        },
    );

    const { data: apiTransactions } = useIntegrationTransactionQuery(
        integrationAccount.id,
        {
            enabled: Boolean(financialDocument.numTransactions),
            refetchOnWindowFocus: false,
        },
    );
    const transactionUpdate = useIntegrationTransactionUpdateMutation();

    const sheetTransactions = useMemo(
        () =>
            apiTransactions?.map((transaction) => ({
                date: formatISO(transaction.date, { representation: "date" }),
                bankDescription: transaction.bankDescription,
                amount: transaction.amount,
            })),
        [apiTransactions],
    );

    const afterChange = useCallback(
        (changes: CellChange[] | null, source: ChangeSource) => {
            if (source === "loadData" || !changes) {
                return;
            }

            const changesByRow = Object.groupBy(changes, (change) => change[0]);

            Object.entries(changesByRow).forEach(([row, rowChanges]) => {
                const existingRow = sheetTransactions?.[Number(row)];

                if (existingRow && rowChanges) {
                    const previousRow = rowChanges.reduce(
                        (acc, [, field, prevValue]) => ({
                            ...acc,
                            [field as any]: prevValue,
                        }),
                        existingRow,
                    );

                    const apiTransaction = apiTransactions?.find(
                        (transaction) =>
                            formatISO(transaction.date, {
                                representation: "date",
                            }) === previousRow.date &&
                            transaction.bankDescription ===
                                previousRow.bankDescription &&
                            transaction.amount === previousRow.amount,
                    );

                    if (!apiTransaction) {
                        return;
                    }

                    const update = rowChanges.reduce(
                        (acc, [, field, , newValue]) => ({
                            ...acc,
                            [field as keyof IntegrationTransactionUpdateBody]:
                                newValue,
                        }),
                        {} as IntegrationTransactionUpdateBody,
                    );

                    if (Object.values(update).some((value) => value === null)) {
                        // value deleting not yet handled
                        return;
                    }

                    transactionUpdate.mutate({
                        body: update,
                        transaction: apiTransaction,
                    });
                }
            });
        },
        [apiTransactions, sheetTransactions, transactionUpdate],
    );

    if (!isTransactionsProcessingFinished(financialDocument)) {
        return (
            <p className={styles.transactionsMessage}>
                We're extracting data from this document. Please wait...
            </p>
        );
    }

    if (!financialDocument.numTransactions) {
        return (
            <p className={styles.transactionsMessage}>
                No transactions found in this document
            </p>
        );
    }

    if (!apiTransactions) {
        return null;
    }

    if (
        financialDocument.importerErrors?.includes(
            ImporterErrors.NOT_CONFIDENT,
        ) &&
        !reviewed.current
    ) {
        reviewed.current = true;
        reviewMutation.mutate();
    }

    return (
        <>
            <ImporterBalanceSummary
                documentRowId={documentRowId}
                financialDocument={financialDocument}
                transactions={apiTransactions}
            />
            <div className={styles.transactionsTable}>
                <HotTable
                    data={sheetTransactions}
                    licenseKey="non-commercial-and-evaluation"
                    autoRowSize={false}
                    rowHeights={30}
                    rowHeaders={true}
                    colHeaders={true}
                    width="100%"
                    height="100%"
                    autoWrapRow={false}
                    autoWrapCol={false}
                    wordWrap={false}
                    minSpareRows={20}
                    fillHandle={true}
                    themeName="ht-theme-kick"
                    afterChange={afterChange}
                >
                    <HotColumn
                        data="date"
                        allowEmpty={true}
                        editor={SpreadsheetDateEditor}
                        title="Date"
                        width={115}
                    />
                    <HotColumn
                        data="bankDescription"
                        type="text"
                        allowEmpty={true}
                        title="Description"
                        width={430}
                    />
                    <HotColumn
                        data="amount"
                        type="numeric"
                        numericFormat={{
                            pattern: "$0,0.00",
                            culture: "en-US",
                        }}
                        className={styles.amountHeader}
                        allowEmpty={true}
                        title="Amount"
                        width={115}
                        id="amount"
                    />
                </HotTable>
            </div>
        </>
    );
};
